@file:Suppress("ktlint:enum-entry-name-case", "EnumEntryName")

package de.katoonerie.contentful

import org.jetbrains.compose.web.css.CSSColorValue
import org.w3c.dom.url.URL

data class Homepage(
    val bannerUrl: URL,
    val colors: Colors,
    val navigationBars: List<NavigationBar>,
    val actionsBar: NavigationBar,
    val introduction: Content.Text,
    val preview: Boolean = false
) {
    data class Colors(
        val background: CSSColorValue,
        val accent: CSSColorValue
    )
}

data class Page(
    val path: String,
    val title: String,
    val navigationBar: NavigationBar?,
    val tiles: List<Tile>,
    val preview: Boolean = false
)

data class Tile(
    val hash: String,
    val title: String,
    val backgroundColor: CSSColorValue,
    val layout: Layout,
    val media: Content.Media,
    val body: Content.Text,
    val gimmicks: List<Gimmick>
) {
    enum class Layout(val type: String) {
        mediaRightOfContent("media-right-of-content"),
        mediaLeftOfContent("media-left-of-content"),
        mediaWithoutContent("media-without-content"),
        unknown("unknown");

        companion object {
            fun valueByType(type: String?): Layout = entries.find { it.type == type } ?: unknown
        }
    }

    data class Gimmick(
        val image: Image,
        val position: Position
    ) {
        enum class Position(val type: String) {
            topLeft("top-left"),
            topCenter("top-center"),
            topRight("top-right"),
            leftTop("left-top"),
            leftCenter("left-center"),
            leftBottom("left-bottom"),
            bottomLeft("bottom-left"),
            bottomCenter("bottom-center"),
            bottomRight("bottom-right"),
            rightTop("right-top"),
            rightCenter("right-center"),
            rightBottom("right-bottom"),
            unknown("unkown");

            companion object {
                fun valueByType(type: String?): Position = entries.find { it.type == type } ?: unknown
            }
        }
    }
}

data class NavigationBar(
    val page: Page,
    val items: List<Item>
) {
    data class Page(
        val path: String,
        val title: String
    )

    data class Item(
        val tile: Tile,
        val color: CSSColorValue,
        val iconUrl: URL
    ) {
        data class Tile(
            val hash: String,
            val title: String
        )
    }
}

data class Asset(
    val url: URL,
    val title: String
)

sealed interface Content {
    val color: CSSColorValue?
    val backgroundColor: CSSColorValue?
    val borderColor: CSSColorValue?

    sealed interface Media : Content, RichText.Node {
        data class YouTube(
            val videos: List<Video>,
            override val color: CSSColorValue? = null,
            override val backgroundColor: CSSColorValue? = null,
            override val borderColor: CSSColorValue? = null
        ) : Media, RichText.Node {
            data class Video(val id: String, val title: String)
        }

        data class Gallery(
            val images: List<Image>,
            override val color: CSSColorValue? = null,
            override val backgroundColor: CSSColorValue? = null,
            override val borderColor: CSSColorValue? = null
        ) : Media, RichText.Node

        data class Actions(
            val actions: List<Action>,
            override val color: CSSColorValue? = null,
            override val backgroundColor: CSSColorValue? = null,
            override val borderColor: CSSColorValue? = null
        ) : Media {
            data class Action(
                val image: Image,
                val archive: Archive
            )
        }
    }

    data class Text(
        val document: RichText.Document,
        override val color: CSSColorValue? = null,
        override val backgroundColor: CSSColorValue? = null,
        override val borderColor: CSSColorValue? = null
    ) : Content
}

data class Image(val url: URL, val title: String) : RichText.Node

data class Archive(val url: URL, val title: String)

sealed interface RichText {
    data class Document(
        val nodes: List<Node>
    )

    sealed interface Node

    data class Paragraph(
        val items: List<Item>
    ) : Node {
        sealed interface Item

        data class Text(
            val value: String
        ) : Item

        data class Hyperlink(
            val href: String,
            val value: String
        ) : Item
    }
}
